import { ChevronRight, FolderOpen } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileNode } from '@/lib/fileUtils';
import { useTranslation } from 'react-i18next';

export interface MoveFilesModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  moveModalPath: FileNode[];
  onPathChange: (path: FileNode[]) => void;
  moveModalCurrentNodes: FileNode[];
  moveTargetPath: string | null;
  onSelectFolder: (node: FileNode) => void;
  onConfirm: () => void;
  isMoving: boolean;
}

export function MoveFilesModal({
  isOpen,
  onOpenChange,
  moveModalPath,
  onPathChange,
  moveModalCurrentNodes,
  moveTargetPath,
  onSelectFolder,
  onConfirm,
  isMoving,
}: MoveFilesModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t('project.moveSelectedItems')}
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            {t('project.chooseDestinationFolder')}
          </p>
        </DialogHeader>

        <div className="px-6 py-4 overflow-hidden">
          <div className="flex items-center gap-2 mb-2 text-sm text-gray-600 overflow-hidden">
            <button
              onClick={() => onPathChange([])}
              className="hover:underline flex-shrink-0"
            >
              {t('project.root')}
            </button>
            {moveModalPath.map((p, i) => (
              <span key={p.id} className="flex items-center gap-1 min-w-0">
                <ChevronRight size={12} className="flex-shrink-0" />
                <button
                  onClick={() => onPathChange(moveModalPath.slice(0, i + 1))}
                  className="hover:underline truncate max-w-[120px]"
                  title={p.name}
                >
                  {p.name}
                </button>
              </span>
            ))}
          </div>

          <div className="max-h-60 overflow-auto border rounded p-2">
            {/* Root directory option */}
            {moveModalPath.length === 0 && (
              <div
                className={`py-2 px-3 rounded cursor-pointer hover:bg-gray-100 flex items-center justify-between mb-1 ${
                  moveTargetPath === '/' ? 'bg-blue-50' : ''
                }`}
                onClick={() =>
                  onSelectFolder({
                    id: '__root__',
                    name: '/',
                    path: '/',
                    type: 'folder',
                  } as FileNode)
                }
              >
                <div className="flex items-center gap-2">
                  <FolderOpen size={14} className="text-yellow-500" />
                  <span className="text-sm text-gray-700 font-medium">
                    / ({t('project.root')})
                  </span>
                </div>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onSelectFolder({
                      id: '__root__',
                      name: '/',
                      path: '/',
                      type: 'folder',
                    } as FileNode);
                  }}
                  className={`px-2 py-0.5 text-xs rounded flex-shrink-0 ${
                    moveTargetPath === '/'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200'
                  }`}
                >
                  {t('project.select')}
                </button>
              </div>
            )}
            {moveModalCurrentNodes.length === 0 && moveModalPath.length > 0 ? (
              <div className="text-sm text-gray-500">
                {t('project.noFolders')}
              </div>
            ) : (
              moveModalCurrentNodes.map(node => (
                <div
                  key={node.id}
                  className={`py-2 px-3 rounded cursor-pointer hover:bg-gray-100 flex items-center justify-between gap-2 ${
                    moveTargetPath === (node.path || node.name)
                      ? 'bg-blue-50'
                      : ''
                  }`}
                  onClick={() => {
                    onPathChange([...moveModalPath, node]);
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FolderOpen
                      size={14}
                      className="text-yellow-500 flex-shrink-0"
                    />
                    <span
                      className="text-sm text-gray-700 truncate"
                      title={node.name}
                    >
                      {node.name}
                    </span>
                  </div>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onSelectFolder(node);
                    }}
                    className={`px-2 py-0.5 text-xs rounded flex-shrink-0 ${
                      moveTargetPath === (node.path || node.name)
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200'
                    }`}
                  >
                    {t('project.select')}
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isMoving}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={onConfirm} disabled={isMoving || !moveTargetPath}>
              {isMoving ? t('project.moving') : t('project.moveHere')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MoveFilesModal;
